import { useState, useEffect, useRef } from 'react';
import { View, Platform, KeyboardAvoidingView, ScrollView, useWindowDimensions, Pressable, Modal } from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { signInAnonymously } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import type { OAuthCredential } from 'firebase/auth';
import { Button } from '@/components/ui/Button';
import { Body, Caption, SubHeading, ThemedText } from '@/components/ui/Text';
import { InputField } from '@/components/ui/InputField';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/theme';
import { TripFlowLogo } from '@/components/graphics/TripFlowLogo';
import { getGoogleClientId, isGoogleSignInConfigured, prefetchGoogleDiscovery, signInWithGoogle } from '@/services/auth/googleAuth';
import { signInWithApple, isAppleSignInAvailable } from '@/services/auth/appleAuth';
import { AccountLinkingRequiredError, signInAndLinkCredential } from '@/services/auth/accountLinking';
import { auth, db } from '@/lib/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { useAuthDraftStore } from '@/state/authDraftStore';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';

type AuthAttemptMethod = 'google' | 'apple' | 'guest' | 'link';
type AuthAttemptState =
  | { status: 'idle' }
  | { status: 'busy'; method: AuthAttemptMethod }
  | { status: 'error'; method: AuthAttemptMethod; message: string };

export default function SignInScreen() {
  const router = useRouter();
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const resetAuthDraft = useAuthDraftStore((s) => s.reset);
  const [appleAvailable, setAppleAvailable] = useState(false);
  const [authAttempt, setAuthAttempt] = useState<AuthAttemptState>({ status: 'idle' });
  const [linkAccount, setLinkAccount] = useState<{ email: string; providerLabel: 'Google' | 'Apple' } | null>(null);
  const [linkPassword, setLinkPassword] = useState('');
  const pendingLinkCredentialRef = useRef<OAuthCredential | null>(null);
  const googleConfigured = isGoogleSignInConfigured();
  const googleClientId = getGoogleClientId();
  const googleLoading = authAttempt.status === 'busy' && authAttempt.method === 'google';
  const appleLoading = authAttempt.status === 'busy' && authAttempt.method === 'apple';
  const guestLoading = authAttempt.status === 'busy' && authAttempt.method === 'guest';
  const linkLoading = authAttempt.status === 'busy' && authAttempt.method === 'link';
  const anyPrimaryAuthBusy =
    authAttempt.status === 'busy' &&
    (authAttempt.method === 'google' || authAttempt.method === 'apple' || authAttempt.method === 'guest');
  const error =
    authAttempt.status === 'error' && authAttempt.method !== 'link' ? authAttempt.message : undefined;
  const linkError =
    authAttempt.status === 'error' && authAttempt.method === 'link' ? authAttempt.message : undefined;

  // Responsive logo sizing: slightly reduced so the icon feels less heavy on dark backgrounds.
  const logoSize = height < 700 ? 184 : 224;

  // Check if Apple Sign-In is available
  useEffect(() => {
    if (Platform.OS === 'ios') {
      isAppleSignInAvailable().then(setAppleAvailable).catch(() => setAppleAvailable(false));
    }
  }, []);

  // Prefetch Google OAuth discovery so "Continue with Google" opens the browser faster
  useEffect(() => {
    if (googleConfigured) {
      prefetchGoogleDiscovery();
    }
  }, [googleConfigured]);

  const handleGoogleSignIn = async () => {
    if (!googleConfigured) return;
    setAuthAttempt({ status: 'busy', method: 'google' });
    try {
      await signInWithGoogle();
    } catch (err) {
      if (err instanceof AccountLinkingRequiredError) {
        pendingLinkCredentialRef.current = err.credential;
        setLinkAccount({ email: err.email, providerLabel: err.providerLabel });
        setAuthAttempt({ status: 'idle' });
        return;
      }
      console.error('[SignIn] Google sign-in error:', err);
      const anyErr = err as { code?: string };
      const firebaseCode = typeof anyErr?.code === 'string' ? anyErr.code : undefined;
      let errorMessage = 'Google sign-in didn’t finish. Check your connection and try again.';
      if (firebaseCode === 'auth/configuration-not-found') {
        errorMessage = 'Google sign-in is not available in this build yet.';
      }
      if (firebaseCode === 'auth/credential-already-in-use') {
        errorMessage = 'This sign-in method could not be used for this account. Try another sign-in option.';
      }
      if (!firebaseCode && err instanceof Error) {
        errorMessage = humanizeAsyncError(err, errorMessage);
      }
      if (!errorMessage.toLowerCase().includes('cancel')) {
        setAuthAttempt({ status: 'error', method: 'google', message: errorMessage });
      } else {
        setAuthAttempt({ status: 'idle' });
      }
    } finally {
      setAuthAttempt((prev) =>
        prev.status === 'busy' && prev.method === 'google' ? { status: 'idle' } : prev,
      );
    }
  };

  const handleAppleSignIn = async () => {
    if (!appleAvailable) {
      setAuthAttempt({ status: 'error', method: 'apple', message: 'Apple Sign-In is only available on iOS 13 or later.' });
      return;
    }

    setAuthAttempt({ status: 'busy', method: 'apple' });
    try {
      await signInWithApple();
      // Navigation is handled by the auth bootstrap once profile hydration completes.
    } catch (err) {
      if (err instanceof AccountLinkingRequiredError) {
        pendingLinkCredentialRef.current = err.credential;
        setLinkAccount({ email: err.email, providerLabel: err.providerLabel });
        setAuthAttempt({ status: 'idle' });
        return;
      }
      console.error('[SignIn] Apple sign-in error:', err);
      const anyErr = err as { code?: string };
      const firebaseCode = typeof anyErr?.code === 'string' ? anyErr.code : undefined;
      let errorMessage = 'Apple sign-in didn’t finish. Check your connection and try again.';
      if (firebaseCode === 'auth/configuration-not-found') {
        errorMessage = 'Apple sign-in is not available in this build yet.';
      }
      if (firebaseCode === 'auth/credential-already-in-use') {
        errorMessage = 'This sign-in method could not be used for this account. Try another sign-in option.';
      }
      if (!firebaseCode && err instanceof Error) {
        errorMessage = humanizeAsyncError(err, errorMessage);
      }
      if (!errorMessage.toLowerCase().includes('cancel')) {
        setAuthAttempt({ status: 'error', method: 'apple', message: errorMessage });
      } else {
        setAuthAttempt({ status: 'idle' });
      }
    } finally {
      setAuthAttempt((prev) =>
        prev.status === 'busy' && prev.method === 'apple' ? { status: 'idle' } : prev,
      );
    }
  };

  const closeLinkAccountModal = () => {
    setLinkAccount(null);
    setLinkPassword('');
    if (authAttempt.status === 'error' && authAttempt.method === 'link') {
      setAuthAttempt({ status: 'idle' });
    }
    pendingLinkCredentialRef.current = null;
  };

  const handleLinkAccountSubmit = async () => {
    if (!linkAccount || !linkPassword.trim()) {
      setAuthAttempt({ status: 'error', method: 'link', message: 'Enter your account password.' });
      return;
    }
    const credential = pendingLinkCredentialRef.current;
    if (!credential) {
      setAuthAttempt({
        status: 'error',
        method: 'link',
        message: 'Session expired. Please try signing in with Google or Apple again.',
      });
      return;
    }
    setAuthAttempt({ status: 'busy', method: 'link' });
    try {
      await signInAndLinkCredential(linkAccount.email, linkPassword, credential);
      setAuthAttempt({ status: 'idle' });
      closeLinkAccountModal();
    } catch (err) {
      console.error('[SignIn] Link account error:', err);
      const msg = humanizeAsyncError(
        err,
        'That password didn’t work for this email. Try again or reset your password.',
      );
      setAuthAttempt({ status: 'error', method: 'link', message: msg });
    } finally {
      setAuthAttempt((prev) =>
        prev.status === 'busy' && prev.method === 'link' ? { status: 'idle' } : prev,
      );
    }
  };

  const handleGuestSignIn = async () => {
    setAuthAttempt({ status: 'busy', method: 'guest' });
    try {
      let cred: any;
      try {
        // Preferred path: Anonymous auth (requires Firebase console enablement).
        cred = await signInAnonymously(auth);
      } catch (anonErr: any) {
        // Fallback: dev-only email/password account, stored locally so we don't create a new account every time.
        const code = String(anonErr?.code || '');
        if (__DEV__ && (code.includes('admin-restricted-operation') || code.includes('operation-not-allowed'))) {
          // Prefer the new Tripvio key, but fall back to the legacy TripFlow key to avoid breaking
          // existing local dev sessions.
          const newKey = '@tripvio_dev_guest_credentials_v1';
          const legacyKey = '@tripflow_dev_guest_credentials_v1';
          const existingRaw = (await AsyncStorage.getItem(newKey)) ?? (await AsyncStorage.getItem(legacyKey));
          const existing = existingRaw ? (() => { try { return JSON.parse(existingRaw); } catch { return null; } })() : null;

          const makeStrongPassword = () => {
            // Meets app rules: 8+ with upper/lower/number/symbol
            const rand = Math.random().toString(36).slice(2);
            return `Tf!${Date.now()}Aa1${rand}`;
          };

          const email =
            typeof existing?.email === 'string' && existing.email.includes('@')
              ? existing.email
              : `dev-guest-${Date.now()}-${Math.random().toString(16).slice(2)}@tripvio.dev`;
          const password =
            typeof existing?.password === 'string' && existing.password.length >= 8
              ? existing.password
              : makeStrongPassword();

          try {
            cred = await signInWithEmailAndPassword(auth, email, password);
          } catch (signInErr: any) {
            // If the account doesn't exist yet, create it.
            cred = await createUserWithEmailAndPassword(auth, email, password);
          }

          await AsyncStorage.setItem(newKey, JSON.stringify({ email, password }));
        } else {
          throw anonErr;
        }
      }

      // DEV-only: mark onboarding complete so we can immediately QA the app.
      if (__DEV__ && cred?.user?.uid) {
        const profileRef = doc(db, 'users', cred.user.uid);
        await setDoc(
          profileRef,
          {
            email: null,
            name: 'Guest',
            onboardingCompleted: true,
            tutorialCompleted: true,
            postOnboardingTutorialEligible: false,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      }

      router.replace('/(tabs)');
    } catch (e: any) {
      console.error('[SignIn] Guest sign-in error:', e);
      setAuthAttempt({
        status: 'error',
        method: 'guest',
        message: humanizeAsyncError(e, 'We couldn’t continue as guest right now. Please try again.'),
      });
    } finally {
      setAuthAttempt((prev) =>
        prev.status === 'busy' && prev.method === 'guest' ? { status: 'idle' } : prev,
      );
    }
  };

  const handlePrivacyPolicy = () => {
    router.push('/(onboarding)/privacy-policy');
  };

  const handleTermsOfService = () => {
    router.push('/(onboarding)/terms-of-service');
  };

  // Icon colors based on button variant
  const primaryIconColor = theme.colorScheme === 'dark' ? '#000000' : theme.colors.surface;
  const secondaryIconColor = theme.colors.primary;

  // Gradient colors for background
  const gradientColors: readonly [string, string, string] = theme.colorScheme === 'dark'
    ? ['#000000', '#0a0f0a', '#132a13'] // Dark mode: black to dark green
    : ['#dde8dd', '#ffffff', '#e8f3e8']; // Light mode: more visible green tint to white to light green

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <LinearGradient
        colors={gradientColors}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
      style={{
        flex: 1,
          paddingTop: Math.max(insets.top + theme.spacing(2), theme.spacing(6)),
          paddingBottom: insets.bottom,
        }}
      >
        {/* Decorative background elements */}
        <View
          style={{
            position: 'absolute',
            top: '10%',
            right: '-10%',
            width: 200,
            height: 200,
            borderRadius: 100,
            backgroundColor: theme.colorScheme === 'dark'
              ? 'rgba(19, 42, 19, 0.15)'
              : 'rgba(19, 42, 19, 0.03)',
            opacity: 0.6,
          }}
          pointerEvents="none"
        />
        <View
          style={{
            position: 'absolute',
            bottom: '15%',
            left: '-5%',
            width: 150,
            height: 150,
            borderRadius: 75,
            backgroundColor: theme.colorScheme === 'dark'
              ? 'rgba(245, 215, 135, 0.08)'
              : 'rgba(245, 215, 135, 0.05)',
            opacity: 0.5,
          }}
          pointerEvents="none"
        />
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            paddingHorizontal: theme.spacing(2),
            paddingVertical: theme.spacing(5),
            paddingTop: Math.max(insets.top, theme.spacing(4)),
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {/* Header Section */}
          <View
            style={{ alignItems: 'center', gap: theme.spacing(0.75), marginBottom: theme.spacing(4) }}
            accessibilityRole="header"
          >
            <View
              style={{
                position: 'relative',
                alignItems: 'center',
                justifyContent: 'center',
                paddingVertical: theme.spacing(0.5),
              }}
            >
              {/* Keep only subtle neutral diffusion so the logo blends naturally. */}
              <View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  width: logoSize * 1.02,
                  height: logoSize * 1.02,
                  borderRadius: (logoSize * 1.02) / 2,
                  backgroundColor: theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(19, 42, 19, 0.05)',
                  opacity: 0.7,
                }}
              />
              <View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  width: logoSize * 1.16,
                  height: logoSize * 1.16,
                  borderRadius: (logoSize * 1.16) / 2,
                  backgroundColor: theme.colorScheme === 'dark' ? 'rgba(0, 0, 0, 0.14)' : 'rgba(19, 42, 19, 0.035)',
                  opacity: 0.55,
                }}
              />
              <View accessible accessibilityLabel="Tripvio logo" accessibilityRole="image">
                <TripFlowLogo
                  size={logoSize}
                  blendWithBackground
                />
              </View>
            </View>
            <Body
              style={{
                textAlign: 'center',
                fontSize: 16,
                lineHeight: 24,
                maxWidth: 320,
              }}
              color={theme.colors.textSecondary}
            >
              Your co-pilot for every adventure. Plan confidently. Travel fearlessly.
        </Body>
      </View>

          {/* Error Display */}
          {error && (
            <View
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.errorContainer,
                borderRadius: theme.radii.md,
                borderLeftWidth: 4,
                borderLeftColor: theme.colors.error,
                marginBottom: theme.spacing(2),
              }}
              accessibilityRole="alert"
              accessibilityLiveRegion="assertive"
            >
              <Caption color={theme.colors.error}>{error}</Caption>
            </View>
          )}

          {!googleConfigured && (
            <View
              style={{
                padding: theme.spacing(1.5),
                backgroundColor: theme.colors.warningContainer || theme.colors.errorContainer,
                borderRadius: theme.radii.md,
                borderLeftWidth: 4,
                borderLeftColor: theme.colors.warning || theme.colors.primary,
                marginBottom: theme.spacing(2),
              }}
              accessibilityRole="text"
              accessibilityLiveRegion="polite"
            >
              <Caption color={theme.colors.text}>
                Google sign-in isn’t available in this build yet.
              </Caption>
            </View>
          )}

          {/* Authentication Buttons */}
          <View style={{ gap: theme.spacing(1.5), marginBottom: theme.spacing(3) }}>
        {appleAvailable && (
          <Button
            label="Continue with Apple"
                icon={<Ionicons name="logo-apple" size={20} color={primaryIconColor} />}
            onPress={handleAppleSignIn}
            loading={appleLoading}
            disabled={anyPrimaryAuthBusy}
                accessibilityLabel="Sign in with Apple"
                accessibilityHint="Double tap to authenticate using your Apple ID"
                accessibilityState={{
                  disabled: anyPrimaryAuthBusy,
                  busy: appleLoading,
                }}
          />
        )}
        <Button
          label="Continue with Google"
          variant="secondary"
              icon={<Ionicons name="logo-google" size={20} color={secondaryIconColor} />}
          onPress={handleGoogleSignIn}
          loading={googleLoading}
          disabled={!googleConfigured || anyPrimaryAuthBusy}
              accessibilityLabel="Sign in with Google"
              accessibilityHint="Double tap to authenticate using your Google account"
              accessibilityState={{
                disabled: !googleConfigured || anyPrimaryAuthBusy,
                busy: googleLoading,
              }}
        />
        <Button
          label="Continue with Email"
          variant="secondary"
              icon={<Ionicons name="mail-outline" size={20} color={secondaryIconColor} />}
          onPress={() => {
            resetAuthDraft({ mode: 'signup' });
            router.push('/(onboarding)/email-auth');
          }}
          disabled={anyPrimaryAuthBusy}
              accessibilityLabel="Sign in with email"
              accessibilityHint="Double tap to sign in using your email address"
              accessibilityState={{
                disabled: anyPrimaryAuthBusy,
              }}
        />
        {__DEV__ && (
          <Button
            label="Continue as Guest (DEV)"
            variant="secondary"
            icon={<Ionicons name="person-outline" size={20} color={secondaryIconColor} />}
            onPress={handleGuestSignIn}
            loading={guestLoading}
            disabled={anyPrimaryAuthBusy}
            accessibilityLabel="Continue as guest"
            accessibilityHint="Signs in anonymously for development testing"
            accessibilityState={{
              disabled: anyPrimaryAuthBusy,
              busy: guestLoading,
            }}
          />
        )}
      </View>

          {/* Terms & Conditions */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
            <Caption
              color={theme.colors.textSecondary}
              style={{ fontSize: 13, lineHeight: 18 }}
            >
              By continuing, you agree to{' '}
      </Caption>
            <Pressable
              onPress={handlePrivacyPolicy}
              accessibilityRole="link"
              accessibilityLabel="Privacy Policy"
              style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
            >
              <ThemedText
                variant="caption"
                color={theme.colors.primary}
                style={{ textDecorationLine: 'underline', fontSize: 13 }}
              >
                Privacy Policy
              </ThemedText>
            </Pressable>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}> and </Caption>
            <Pressable
              onPress={handleTermsOfService}
              accessibilityRole="link"
              accessibilityLabel="Terms of Service"
              style={{ paddingVertical: theme.spacing(0.5), paddingHorizontal: theme.spacing(0.25), minHeight: 44, justifyContent: 'center' }}
            >
              <ThemedText
                variant="caption"
                color={theme.colors.primary}
                style={{ textDecorationLine: 'underline', fontSize: 13 }}
              >
                Terms of Service
              </ThemedText>
            </Pressable>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>.</Caption>
    </View>
        </ScrollView>
      </LinearGradient>

      {/* Link Account modal: sign in with email/password to link Google or Apple */}
      <Modal visible={!!linkAccount} transparent animationType="fade" onRequestClose={closeLinkAccountModal}>
        <Pressable
          onPress={closeLinkAccountModal}
          style={{
            flex: 1,
            backgroundColor: 'rgba(0,0,0,0.45)',
            justifyContent: 'center',
            padding: theme.spacing(3),
          }}
        >
          <Pressable onPress={() => {}} style={{ width: '100%', maxWidth: 420, alignSelf: 'center' }}>
            <Card variant="outlined" padding={theme.spacing(2.5)}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing(2) }}>
                <View style={{ flex: 1, paddingRight: theme.spacing(2) }}>
                  <SubHeading>Link your {linkAccount?.providerLabel ?? ''} account</SubHeading>
                  <Body color={theme.colors.textSecondary} style={{ marginTop: theme.spacing(0.5), lineHeight: 20 }}>
                    An account with this email already exists. Enter your password to link your {linkAccount?.providerLabel ?? ''} sign-in to that account.
                  </Body>
                </View>
                <Pressable onPress={closeLinkAccountModal} accessibilityRole="button" accessibilityLabel="Close" style={{ padding: theme.spacing(0.5) }}>
                  <Ionicons name="close" size={22} color={theme.colors.textSecondary} />
                </Pressable>
              </View>
              {linkAccount && (
                <>
                  <View style={{ marginBottom: theme.spacing(1.5) }}>
                    <Body color={theme.colors.textSecondary}>Email</Body>
                    <Body style={{ marginTop: theme.spacing(0.25) }}>{linkAccount.email}</Body>
                  </View>
                  <InputField
                    label="Password"
                    placeholder="Your account password"
                    value={linkPassword}
                    onChangeText={setLinkPassword}
                    secureTextEntry
                    showPasswordToggle
                    errorText={linkError}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <View style={{ flexDirection: 'row', gap: theme.spacing(1.5), marginTop: theme.spacing(2) }}>
                    <Button label="Cancel" variant="secondary" onPress={closeLinkAccountModal} style={{ flex: 1 }} />
                    <Button
                      label="Link account"
                      onPress={handleLinkAccountSubmit}
                      loading={linkLoading}
                      disabled={linkLoading}
                      style={{ flex: 1 }}
                    />
                  </View>
                </>
              )}
            </Card>
          </Pressable>
        </Pressable>
      </Modal>
    </KeyboardAvoidingView>
  );
}


